import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useSelector } from '_common/hooks';
import { useGetTemplatesListQuery } from '../../TemplatesApi';
import { useFilterSelector } from '_common/components/Filters/FilterSlice';

import { EmptyFilteredState } from '_common/components';
import { Table, Cell, type TableColumn, type TableRow } from '_common/components/Table';

import TemplateEmptyState from './TemplateEmptyState/TemplateEmptyState';
import ActionsCell from './Cells/ActionsCell/ActionsCell';
import NameCell from './Cells/NameCell/NameCell';

import styles from './TemplateTable.module.scss';
import VersionCell from './Cells/VersionCell/VersionCell';

type TemplatesTableProps = {
  category: Template['category'];
  search: string;
};

const TemplateTable = ({ category, search }: TemplatesTableProps) => {
  const intl = useIntl();

  const { data, isFetching } = useGetTemplatesListQuery();

  const { selectFilterParams } = useFilterSelector();
  const filterParams = useSelector((state) =>
    selectFilterParams(state, state.filters[`templates-${category}`]),
  );

  const filteredTemplates = useMemo(() => {
    if (!data) {
      return [];
    }
    let filteredTemplates = data.order;

    // Filters by name
    if (search !== '') {
      filteredTemplates = filteredTemplates.filter((templateId) =>
        data.extensions[templateId].name.toLowerCase().includes(search.toLowerCase()),
      );
    }

    // Filter by category
    filteredTemplates = filteredTemplates.filter(
      (templateId) => data.extensions[templateId].category === category,
    );

    // Filters by status if any filter is active
    return filterParams.filter_fields.length > 0
      ? filteredTemplates.filter((templateId) =>
          filterParams.filter_fields.includes(data.extensions[templateId].status),
        )
      : filteredTemplates;
  }, [data, search, filterParams]);

  const columns = useMemo<TableColumn[]>(
    () =>
      data
        ? [
            {
              id: 'name',
              accessorKey: 'name',
              header: intl.formatMessage({ id: 'global.name' }),
              size: 200,
              cell: (info) => {
                const template = data.extensions[info.row.id];
                return (
                  <Cell testId={`${template.id}-name-column`}>
                    <NameCell templateId={template.id} />
                  </Cell>
                );
              },
            },
            {
              id: 'description',
              accessorKey: 'description',
              enableSorting: false,
              header: intl.formatMessage({ id: 'global.description' }),
              cell: (info) => {
                const template = data.extensions[info.row.id];
                return (
                  <Cell testId={`${template.id}-description-column`}>
                    {template.description ? (
                      template.description
                    ) : (
                      <Cell.Empty>{intl.formatMessage({ id: 'NO_DESCRIPTION' })}</Cell.Empty>
                    )}
                  </Cell>
                );
              },
            },
            {
              id: 'version',
              accessorKey: 'installed_version',
              enableSorting: false,
              size: 120,
              header: intl.formatMessage({ id: 'VERSION' }),
              cell: (info) => {
                const template = data.extensions[info.row.id];
                return (
                  <Cell testId={`${template.id}-version-column`}>
                    <VersionCell templateId={template.id} />
                  </Cell>
                );
              },
            },
          ]
        : [],
    [data],
  );

  const rowsData = useMemo(
    () =>
      data
        ? filteredTemplates.map<TableRow>((templateId) => {
            const template = data.extensions[templateId];
            return {
              ...template,
              suffix: {
                children: <ActionsCell templateId={template.id} />,
              },
            };
          })
        : [],
    [filteredTemplates, data],
  );

  if (filteredTemplates.length === 0) {
    return filterParams.filter_fields.length > 0 ? (
      <EmptyFilteredState identity={`templates-${category}`} margin="8rem 0" />
    ) : (
      <TemplateEmptyState />
    );
  }

  return (
    <div className={styles.root}>
      <Table
        identity="templates"
        options={{ columns, data: rowsData }}
        renderFooter={() => null}
        isLoading={isFetching}
        loadingLabel={intl.formatMessage({ id: 'LOADING_ELEMENTS' })}
        testId={`templates-${category}`}
        selectable={false}
        height={'100%'}
      />
    </div>
  );
};

export default TemplateTable;
